import {
  Container,
  Table,
  Badge,
  Title,
  Text,
  Stack,
  Card,
  Box,
  Group,
  Divider,
} from '@mantine/core';
import { CheckIcon, XIcon } from 'lucide-react';
import { ReactNode } from 'react';
import React from 'react';

interface ComparisonFeature {
  feature: string;
  practist: boolean | string;
  clio: boolean | string;
  mycase: boolean | string;
  lawmatics: boolean | string;
}

interface ComparisonSection {
  title: string;
  features: ComparisonFeature[];
}

/** Update when you refresh the comparison table for marketing accuracy. */
const COMPARISON_FINE_PRINT_AS_OF = 'April 2026';

/** Same soft Practist tint in light and dark mode (Mantine's `practistBlue-0` token shifts in dark scheme). */
const PRACTIST_COLUMN_BG = '#eaefff';

const COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    title: 'Intake',
    features: [
      {
        feature: 'Intake and open cases in same system (no exporting)',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: false,
      },
      {
        feature: 'Create dynamic, embeddable intake forms',
        practist: true,
        clio: true,
        mycase: true,
        lawmatics: true,
      },
      {
        feature: 'Built-in automated engagement letters',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: false,
      },
      {
        feature: 'Easily send invoices',
        practist: true,
        clio: true,
        mycase: true,
        lawmatics: true,
      },
    ],
  },
  {
    title: 'AI',
    features: [
      {
        feature: 'Forward email to open case',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: false,
      },
      {
        feature: 'Assistant can do anything a human can within the app',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: 'Not launched yet.',
      },
      {
        feature:
          "AI evaluates qualification of incoming leads based on firm's criteria.",
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: true,
      },
      {
        feature: 'Agentic AI sends follow-up emails to client prospects.',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: 'Not launched yet.',
      },
      {
        feature: 'Confidential: LLM not trained on client or firm data',
        practist: true,
        clio: true,
        mycase: true,
        lawmatics: true,
      },
      {
        feature: 'AI document drafting tools',
        practist: true,
        clio: 'Additional charge',
        mycase: true,
        lawmatics: false,
      },
    ],
  },
  {
    title: 'Documents',
    features: [
      {
        feature: 'Save directly from Word and Google Docs',
        practist: true,
        clio: 'Requires additional app',
        mycase: 'Requires additional app',
        lawmatics: false,
      },
      {
        feature: 'Retain possession of your documents without migration',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: false,
      },
    ],
  },
  {
    title: 'Alternative Fees',
    features: [
      {
        feature: 'Built-in flat fee handling',
        practist: true,
        clio: 'Requires workarounds',
        mycase: true,
        lawmatics: 'Via Time & Billing add-on',
      },
      {
        feature: 'Built-in subscription fee handling',
        practist: true,
        clio: 'Requires workarounds',
        mycase: 'Requires workarounds',
        lawmatics: 'Via Time & Billing add-on',
      },
      {
        feature: 'Built-in legal insurance handling',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: false,
      },
    ],
  },
  {
    title: 'Payment Processing',
    features: [
      {
        feature: 'Integrated with invoices',
        practist: true,
        clio: true,
        mycase: true,
        lawmatics: 'With Time & Billing / LMPay',
      },
      {
        feature: 'Credit processing rates',
        practist: '2.99% + $0.30',
        clio: '2.95%/3.5%',
        mycase: '2.95% + $0.20/\n3.75% + $0.20',
        lawmatics: '2.95%/3.25%',
      },
      {
        feature: 'ACH rates',
        practist: '1%',
        clio: '1%',
        mycase: '1% up to $10/transaction',
        lawmatics: '0.5% (Beginning January 1, 2026)',
      },
      {
        feature: 'No extra charge for American Express',
        practist: true,
        clio: false,
        mycase: false,
        lawmatics: false,
      },
      {
        feature: 'No card brand network fees',
        practist: true,
        clio: true,
        mycase: false,
        lawmatics: 'Contact sales',
      },
    ],
  },
  {
    title: 'Pricing',
    features: [
      {
        feature: 'All-in subscription pricing',
        practist: '$20/$30',
        clio: '$149/$169',
        mycase: '$109/$119',
        lawmatics: 'Custom quote',
      },
      {
        feature: 'Migration support',
        practist: 'Yes with annual plan',
        clio: 'Additional charge',
        mycase: "Add'l charge for docs",
        lawmatics: 'Contact sales',
      },
    ],
  },
];

const JumboTitle = ({
  children,
  order = 1,
  fz = 'xl',
  ...otherProps
}: {
  children: ReactNode;
  order?: 1 | 2 | 3 | 4 | 5;
  fz?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  [key: string]: any;
}) => {
  const FONT_WEIGHT_MAP: Record<string, number> = {
    xl: 800,
    lg: 800,
    md: 700,
    sm: 600,
    xs: 500,
  };

  const FONT_SIZE_MAP: Record<string, string> = {
    xl: 'clamp(2.5rem, 5vw + 1rem, 4rem)',
    lg: 'clamp(2rem, 4vw + 1rem, 3.5rem)',
    md: 'clamp(1.75rem, 3vw + 1rem, 3rem)',
    sm: 'clamp(1.5rem, 2.5vw + 1rem, 2.5rem)',
    xs: 'clamp(1.25rem, 2vw + 1rem, 2rem)',
  };

  const { style: otherStyle, ...restProps } = otherProps;

  return (
    <Title
      fw={FONT_WEIGHT_MAP[fz]}
      lh={1}
      order={order}
      style={{
        fontSize: FONT_SIZE_MAP[fz],
        ...otherStyle,
      }}
      {...restProps}
    >
      {children}
    </Title>
  );
};

const ComparisonFinePrint = () => (
  <Box mt='lg' w='100%' maw='100%' aria-label='Comparison disclaimer'>
    <Text size='xs' c='dimmed' lh={1.55} component='p' m={0}>
      This chart is a simplified, high-level comparison for prospective
      customers. It summarizes selected capabilities, fees, and pricing based on
      each vendor&apos;s public websites, help documentation, and similar
      materials, to the best of our understanding as of{' '}
      {COMPARISON_FINE_PRINT_AS_OF}. Products and terms change often; the table
      is not exhaustive and may omit plan tiers, add-ons, bundles, promotions,
      integrations, or jurisdiction-specific requirements. Before you rely on
      any row, including for Practist, confirm current features, fees, and
      contract terms directly with each provider.
    </Text>
  </Box>
);

const ComparisonChart = () => {
  const renderValue = (value: boolean | string, isPractist = false) => {
    if (value === true) {
      return (
        <Badge
          color='green'
          variant='light'
          size='lg'
          leftSection={<CheckIcon size={16} />}
        >
          Yes
        </Badge>
      );
    }
    if (value === false) {
      return (
        <Badge
          color='red'
          variant='light'
          size='lg'
          leftSection={<XIcon size={16} />}
        >
          No
        </Badge>
      );
    }
    if (typeof value === 'string') {
      // Handle strings with line breaks
      if (value.includes('\n')) {
        return (
          <Text size='md' fw={500} c={isPractist ? 'practistBlue' : 'dimmed'}>
            {value.split('\n').map((line, index, array) => (
              <React.Fragment key={index}>
                {line}
                {index < array.length - 1 && <br />}
              </React.Fragment>
            ))}
          </Text>
        );
      }

      // Bold dollar amounts (pricing)
      if (value.trim().startsWith('$')) {
        return (
          <Text size='md' fw={700} c={isPractist ? 'practistBlue' : 'dimmed'}>
            {value}
          </Text>
        );
      }
    }

    return (
      <Text size='md' fw={500} c={isPractist ? 'practistBlue' : 'dimmed'}>
        {value}
      </Text>
    );
  };

  return (
    <Container
      id='compare'
      size='lg'
      pt={{
        base: 'calc(var(--mantine-spacing-lg) * 4)',
        xs: 'calc(var(--mantine-spacing-lg) * 5)',
        lg: 'calc(var(--mantine-spacing-lg) * 6)',
      }}
      pb={{
        base: 'calc(var(--mantine-spacing-lg) * 2)',
        xs: 'calc(var(--mantine-spacing-lg) * 2)',
        lg: 'calc(var(--mantine-spacing-lg) * 2)',
      }}
    >
      <Stack gap='xl'>
        <Stack align='center' gap='xs'>
          <JumboTitle
            order={2}
            fz='md'
            ta='center'
            style={{ textWrap: 'balance' }}
          >
            Compare Practist to the Competition
          </JumboTitle>
          <Text c='dimmed' ta='center' fz='lg' style={{ textWrap: 'balance' }}>
            See how Practist stacks up against other practice management options
          </Text>
        </Stack>

        {/* Desktop Table View */}
        <Card shadow='md' padding='xl' radius='lg' withBorder visibleFrom='md'>
          <Table.ScrollContainer
            minWidth={780}
            type='native'
            style={{ scrollbarGutter: 'stable' }}
          >
            <Table
              highlightOnHover
              withTableBorder
              withColumnBorders
              style={{ tableLayout: 'auto' }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '26%' }}>
                    <Text fw={600} size='md'>
                      Feature
                    </Text>
                  </Table.Th>
                  <Table.Th style={{ width: '18.5%', textAlign: 'center' }}>
                    <Text
                      fw={700}
                      size='md'
                      variant='gradient'
                      gradient={{
                        from: 'pink.6',
                        to: 'practistBlue.6',
                        deg: 90,
                      }}
                    >
                      Practist
                    </Text>
                  </Table.Th>
                  <Table.Th style={{ width: '18.5%', textAlign: 'center' }}>
                    <Text fw={600} size='md'>
                      Clio
                    </Text>
                  </Table.Th>
                  <Table.Th style={{ width: '18.5%', textAlign: 'center' }}>
                    <Text fw={600} size='md'>
                      MyCase
                    </Text>
                  </Table.Th>
                  <Table.Th style={{ width: '18.5%', textAlign: 'center' }}>
                    <Text fw={600} size='md'>
                      Lawmatics
                    </Text>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {COMPARISON_SECTIONS.map((section, sectionIndex) => (
                  <React.Fragment key={sectionIndex}>
                    <Table.Tr>
                      <Table.Td colSpan={5}>
                        <Text
                          fw={700}
                          size='sm'
                          c='practistBlue'
                          style={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {section.title}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                    {section.features.map((row, featureIndex) => (
                      <Table.Tr key={`${sectionIndex}-${featureIndex}`}>
                        <Table.Td>
                          <Text
                            fw={500}
                            size='md'
                            style={{ paddingLeft: 'var(--mantine-spacing-md)' }}
                          >
                            {row.feature}
                          </Text>
                        </Table.Td>
                        <Table.Td
                          style={{
                            textAlign: 'center',
                            backgroundColor: PRACTIST_COLUMN_BG,
                          }}
                        >
                          {renderValue(row.practist, true)}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          {renderValue(row.clio)}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          {renderValue(row.mycase)}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          {renderValue(row.lawmatics)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </React.Fragment>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <ComparisonFinePrint />
        </Card>

        {/* Mobile Card View */}
        <Stack gap='lg' hiddenFrom='md'>
          {COMPARISON_SECTIONS.map((section, sectionIndex) => (
            <Card
              key={sectionIndex}
              shadow='sm'
              padding='lg'
              radius='md'
              withBorder
            >
              <Stack gap='md'>
                <Text
                  fw={700}
                  size='sm'
                  c='practistBlue'
                  style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  {section.title}
                </Text>
                <Divider />
                {section.features.map((row, featureIndex) => (
                  <Box key={featureIndex}>
                    <Text fw={500} size='md' mb='xs'>
                      {row.feature}
                    </Text>
                    <Group gap='sm' mt='xs' wrap='wrap'>
                      <Box
                        style={{
                          flex: '1 1 100%',
                          padding: 'var(--mantine-spacing-sm)',
                          borderRadius: 'var(--mantine-radius-md)',
                          backgroundColor: PRACTIST_COLUMN_BG,
                        }}
                      >
                        <Text size='xs' c='dimmed' mb={4} fw={600}>
                          Practist
                        </Text>
                        {renderValue(row.practist, true)}
                      </Box>
                      {(
                        [
                          ['Clio', row.clio],
                          ['MyCase', row.mycase],
                          ['Lawmatics', row.lawmatics],
                        ] as const
                      ).map(([label, value]) => (
                        <Box
                          key={label}
                          style={{
                            flex: '1 1 calc(50% - var(--mantine-spacing-sm) / 2)',
                            minWidth: '8.5rem',
                          }}
                        >
                          <Text size='xs' c='dimmed' mb={4} fw={600}>
                            {label}
                          </Text>
                          {renderValue(value)}
                        </Box>
                      ))}
                    </Group>
                    {featureIndex < section.features.length - 1 && (
                      <Divider mt='md' />
                    )}
                  </Box>
                ))}
              </Stack>
            </Card>
          ))}
        </Stack>

        <Box px='lg' hiddenFrom='md'>
          <ComparisonFinePrint />
        </Box>
      </Stack>
    </Container>
  );
};

export default ComparisonChart;
